import { z } from "zod";
import { MAX_PAGE_SIZE, paginatedSchema } from "../common/pagination.js";
import { idSchema, isoDateTimeSchema, userSummarySchema } from "../common/primitives.js";
import {
  DATE_ORDER_ERROR,
  datesInOrder,
  isoDateSchema,
  projectParamsSchema,
} from "../projects/project.schemas.js";
import { taskTagSchema } from "./tags.schemas.js";

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

/** Every project status belongs to one category; categories drive completion and workload. */
export const TASK_STATUS_CATEGORIES = ["BACKLOG", "ACTIVE", "DONE", "CANCELLED"] as const;
export type TaskStatusCategory = (typeof TASK_STATUS_CATEGORIES)[number];
export const taskStatusCategorySchema = z.enum(TASK_STATUS_CATEGORIES);

/** Categories whose tasks are finished (they count as neither open nor overdue). */
export const CLOSED_STATUS_CATEGORIES: readonly TaskStatusCategory[] = ["DONE", "CANCELLED"];

/** Statuses every new project starts with, in board order. */
export const DEFAULT_TASK_STATUSES: readonly { name: string; category: TaskStatusCategory }[] = [
  { name: "Backlog", category: "BACKLOG" },
  { name: "To do", category: "ACTIVE" },
  { name: "In progress", category: "ACTIVE" },
  { name: "Done", category: "DONE" },
  { name: "Cancelled", category: "CANCELLED" },
];

/** Ordered from most to least urgent. */
export const TASK_PRIORITIES = ["URGENT", "HIGH", "MEDIUM", "LOW", "NONE"] as const;
export type TaskPriority = (typeof TASK_PRIORITIES)[number];
export const taskPrioritySchema = z.enum(TASK_PRIORITIES);

export const TASK_PRIORITY_LABELS: Readonly<Record<TaskPriority, string>> = {
  URGENT: "Urgent",
  HIGH: "High",
  MEDIUM: "Medium",
  LOW: "Low",
  NONE: "No priority",
};

/** Upper bound for a task's duration: 1,000 hours. */
export const MAX_TASK_DURATION_MINUTES = 1000 * 60;
/** Estimated time to do a task, in whole minutes. */
export const taskDurationSchema = z
  .number()
  .int("Duration must be whole minutes")
  .positive("Duration must be more than zero")
  .max(MAX_TASK_DURATION_MINUTES, "Duration must be at most 1,000 hours");

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

export const taskStatusSchema = z.object({
  id: idSchema,
  name: z.string(),
  category: taskStatusCategorySchema,
  position: z.number().int(),
});
export type TaskStatus = z.infer<typeof taskStatusSchema>;

/** The parent shown on a subtask. */
export const taskParentSchema = z.object({
  id: idSchema,
  key: z.string(),
  title: z.string(),
});
export type TaskParent = z.infer<typeof taskParentSchema>;

/** Subtask progress, computed from the subtasks' status categories (never stored). */
export const subtaskProgressSchema = z.object({
  total: z.number().int().nonnegative(),
  /** Subtasks in a DONE-category status. */
  done: z.number().int().nonnegative(),
});
export type SubtaskProgress = z.infer<typeof subtaskProgressSchema>;

/**
 * Dependencies are stored once, as `source <type> target` (`A BLOCKS B`); the inverse
 * (`B BLOCKED_BY A`) is derived. Only the stored types are enumerated here.
 */
export const DEPENDENCY_TYPES = ["BLOCKS", "RELATES_TO", "DUPLICATES"] as const;
export type DependencyType = (typeof DEPENDENCY_TYPES)[number];
export const dependencyTypeSchema = z.enum(DEPENDENCY_TYPES);

/** Types a client may send: `BLOCKED_BY` is stored as the inverse `BLOCKS`. */
export const DEPENDENCY_REQUEST_TYPES = [...DEPENDENCY_TYPES, "BLOCKED_BY"] as const;
export type DependencyRequestType = (typeof DEPENDENCY_REQUEST_TYPES)[number];
export const dependencyRequestTypeSchema = z.enum(DEPENDENCY_REQUEST_TYPES);

/** The other task of a dependency, with just enough to render and link to it. */
export const taskRefSchema = z.object({
  id: idSchema,
  key: z.string(),
  title: z.string(),
  statusCategory: taskStatusCategorySchema,
});
export type TaskRef = z.infer<typeof taskRefSchema>;

/** One dependency seen from a task. `direction` tells which side of the stored row this task is. */
export const dependencyRefSchema = z.object({
  /** Id of the dependency row; use it to remove the dependency. */
  id: idSchema,
  type: dependencyTypeSchema,
  /** OUTGOING: this task is the source (`this BLOCKS other`). INCOMING: the other task is. */
  direction: z.enum(["OUTGOING", "INCOMING"]),
  task: taskRefSchema,
});
export type DependencyRef = z.infer<typeof dependencyRefSchema>;

/** Checklist progress across all of a task's checklists. */
export const checklistProgressSchema = z.object({
  total: z.number().int().nonnegative(),
  done: z.number().int().nonnegative(),
});
export type ChecklistProgress = z.infer<typeof checklistProgressSchema>;

export const taskSchema = z.object({
  id: idSchema,
  workspaceId: idSchema,
  projectId: idSchema,
  number: z.number().int().positive(),
  /** Derived `PROJECT_KEY-number`, e.g. `WEB-12`; never stored. */
  key: z.string(),
  /** Set on subtasks. Subtasks can't have subtasks of their own. */
  parent: taskParentSchema.nullable(),
  subtasks: subtaskProgressSchema,
  checklist: checklistProgressSchema,
  tags: z.array(taskTagSchema),
  /** Tasks that must finish before this one (`BLOCKS` seen from the other side). */
  blockedBy: z.array(dependencyRefSchema),
  /** Tasks this one blocks. */
  blocks: z.array(dependencyRefSchema),
  /** `RELATES_TO` and `DUPLICATES` links, in either direction. */
  related: z.array(dependencyRefSchema),
  title: z.string(),
  description: z.string().nullable(),
  statusId: idSchema,
  priority: taskPrioritySchema,
  /** Everyone the task is assigned to; empty when unassigned. */
  assignees: z.array(userSummarySchema),
  creator: userSummarySchema.nullable(),
  startDate: isoDateSchema.nullable(),
  dueDate: isoDateSchema.nullable(),
  durationMinutes: taskDurationSchema.nullable(),
  completedAt: isoDateTimeSchema.nullable(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type Task = z.infer<typeof taskSchema>;

export const workloadRowSchema = z.object({
  /** `null` groups unassigned tasks. */
  assignee: userSummarySchema.nullable(),
  /** Open (not done or cancelled) tasks per status id. */
  openByStatus: z.record(z.string(), z.number().int().nonnegative()),
  /** Finished (DONE-category) tasks per status id. */
  doneByStatus: z.record(z.string(), z.number().int().nonnegative()),
  open: z.number().int().nonnegative(),
  overdue: z.number().int().nonnegative(),
  dueSoon: z.number().int().nonnegative(),
  completed: z.number().int().nonnegative(),
  /** Total duration of open tasks, in minutes (tasks without a duration count as zero). */
  openMinutes: z.number().int().nonnegative(),
  /** Total duration of finished tasks, in minutes. */
  doneMinutes: z.number().int().nonnegative(),
});
export type WorkloadRow = z.infer<typeof workloadRowSchema>;

/** A task assigned to the caller, with just enough of its project and status to link to it. */
export const myTaskSchema = z.object({
  id: idSchema,
  key: z.string(),
  title: z.string(),
  priority: taskPrioritySchema,
  dueDate: isoDateSchema.nullable(),
  status: taskStatusSchema.pick({ id: true, name: true, category: true }),
  project: z.object({ id: idSchema, key: z.string(), name: z.string() }),
});
export type MyTask = z.infer<typeof myTaskSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

const taskTitleSchema = z
  .string()
  .trim()
  .min(1, "Title is required")
  .max(200, "Title must be at most 200 characters");
const taskDescriptionSchema = z
  .string()
  .trim()
  .max(10_000, "Description must be at most 10,000 characters");

/** The most people one task can be assigned to. */
export const MAX_TASK_ASSIGNEES = 20;
const taskAssigneeIdsSchema = z
  .array(idSchema)
  .max(MAX_TASK_ASSIGNEES, `Assign at most ${String(MAX_TASK_ASSIGNEES)} people`);

/** The most subtasks one create request may add along with their parent. */
export const MAX_NEW_SUBTASKS = 50;

export const createTaskSchema = z
  .object({
    title: taskTitleSchema,
    /** Create this task as a subtask of another task in the same project. */
    parentTaskId: idSchema.optional(),
    /** Subtasks to create with the task, in order, in the same transaction. */
    subtasks: z
      .array(z.object({ title: taskTitleSchema }))
      .max(MAX_NEW_SUBTASKS, `Add at most ${String(MAX_NEW_SUBTASKS)} subtasks at once`)
      .optional(),
    description: taskDescriptionSchema.optional(),
    statusId: idSchema.optional(),
    priority: taskPrioritySchema.optional(),
    assigneeIds: taskAssigneeIdsSchema.optional(),
    startDate: isoDateSchema.nullable().optional(),
    dueDate: isoDateSchema.nullable().optional(),
    durationMinutes: taskDurationSchema.nullable().optional(),
  })
  .refine(datesInOrder, DATE_ORDER_ERROR)
  .refine((value) => !value.parentTaskId || !value.subtasks?.length, {
    error: "Subtasks can't have subtasks of their own",
    path: ["subtasks"],
  });
export type CreateTaskInput = z.infer<typeof createTaskSchema>;

export const updateTaskSchema = z
  .object({
    title: taskTitleSchema.optional(),
    description: taskDescriptionSchema.nullable().optional(),
    statusId: idSchema.optional(),
    priority: taskPrioritySchema.optional(),
    /** Replaces the whole set of assignees. */
    assigneeIds: taskAssigneeIdsSchema.optional(),
    startDate: isoDateSchema.nullable().optional(),
    dueDate: isoDateSchema.nullable().optional(),
    durationMinutes: taskDurationSchema.nullable().optional(),
    /**
     * Optimistic concurrency: the task's `updatedAt` as the client last saw it. When it no longer
     * matches (someone else changed the task), the update is rejected with 409 CONFLICT.
     */
    updatedAt: isoDateTimeSchema.optional(),
  })
  .refine(
    (value) => Object.entries(value).some(([field, v]) => field !== "updatedAt" && v !== undefined),
    { error: "Provide at least one field to update" },
  )
  .refine(datesInOrder, DATE_ORDER_ERROR);
export type UpdateTaskInput = z.infer<typeof updateTaskSchema>;

/** The most tags one list filter may name. */
export const MAX_TAG_FILTER = 20;

/** `assigneeId=<user>` lists tasks assigned to that user (among others); `none` lists unassigned tasks. */
export const UNASSIGNED = "none";
/**
 * `parentTaskId=none` lists top-level tasks only (what the List and Board show). Their search
 * and assignee filters also match through subtasks, returning the parent.
 */
export const NO_PARENT = "none";

export const listTasksQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(100),
  cursor: z.string().min(1).max(512).optional(),
  statusId: idSchema.optional(),
  assigneeId: z.union([idSchema, z.literal(UNASSIGNED)]).optional(),
  search: z.string().trim().max(200).optional(),
  /** Only the subtasks of this task, or `none` for top-level tasks. */
  parentTaskId: z.union([idSchema, z.literal(NO_PARENT)]).optional(),
  /** Comma-separated tag ids: tasks carrying any of them. */
  tagIds: z
    .string()
    .max(40 * MAX_TAG_FILTER)
    .transform((value) => value.split(",").filter((part) => part.length > 0))
    .pipe(z.array(idSchema).min(1).max(MAX_TAG_FILTER))
    .optional(),
});
export type ListTasksQuery = z.infer<typeof listTasksQuerySchema>;
export interface ListTasksQueryInput {
  limit?: number | undefined;
  cursor?: string | undefined;
  statusId?: string | undefined;
  assigneeId?: string | undefined;
  search?: string | undefined;
  parentTaskId?: string | undefined;
  /** Comma-separated tag ids. */
  tagIds?: string | undefined;
}

export const taskParamsSchema = projectParamsSchema.extend({ taskId: idSchema });

// ---------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------

export const tasksResponseSchema = paginatedSchema(taskSchema);
export type TasksResponse = z.infer<typeof tasksResponseSchema>;

export const taskResponseSchema = z.object({ task: taskSchema });
export type TaskResponse = z.infer<typeof taskResponseSchema>;

export const taskStatusesResponseSchema = z.object({ statuses: z.array(taskStatusSchema) });
export type TaskStatusesResponse = z.infer<typeof taskStatusesResponseSchema>;

/** The caller's open tasks across every project they can see, most pressing first. */
export const myWorkResponseSchema = z.object({
  /** The workspace-local date used for overdue / due-soon, `YYYY-MM-DD`. */
  today: isoDateSchema,
  /** Counts cover all of the caller's open tasks; `tasks` is only the first few. */
  open: z.number().int().nonnegative(),
  overdue: z.number().int().nonnegative(),
  /** Due from today through the next 7 days. */
  dueSoon: z.number().int().nonnegative(),
  tasks: z.array(myTaskSchema),
});
export type MyWorkResponse = z.infer<typeof myWorkResponseSchema>;

export const workloadResponseSchema = z.object({
  /** The workspace-local date used for overdue / due-soon, `YYYY-MM-DD`. */
  today: isoDateSchema,
  rows: z.array(workloadRowSchema),
});
export type WorkloadResponse = z.infer<typeof workloadResponseSchema>;
